import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// ─── Auth ───────────────────────────────────────────────────────────────────
import UnifiedAuth from '../features/auth/pages/UnifiedAuth';

// ─── Shared (Dashboard — accessible to all roles) ───────────────────────────
import Dashboard from '../features/dashboard/pages/Dashboard';
import PresentationConfig from '../features/Presentation_config/pages/PresentationConfig';

// ─── Admin Role ──────────────────────────────────────────────────────────────
import UsersRoles from '../features/admin/pages/UsersRoles';
import UserDetails from '../features/admin/pages/UserDetails';

// ─── Bid Manager (Team Lead) Role ────────────────────────────────────────────
import BidManagerDashboard from '../features/proposals/pages/Proposals';
import RfpDetails from '../features/rfp/team-lead/analysis/RFPAnalysisResult';
import PPTCustomize from '../features/ppt/pages/PPTCustomization';
import PPTThemePage from '../features/ppt/pages/PPTThemePage';
import Projects from '../features/projects/pages/Projects';
import Analytics from '../features/analytics/pages/Analytics';
import ContentLibrary from '../features/library/pages/ContentLibrary';
import Team from '../features/team/pages/Team';
import Settings from '../features/settings/pages/Settings';
import AuditLogs from '../features/audit-logs/pages/AuditLogs';

// ─── Editor (Team Member) Role ───────────────────────────────────────────────
import EditorDashboard from '../features/proposals/pages/MyProposals';
import RFPIntake from '../features/rfp/shared/RFPIntake';
import Requirements from '../features/rfp/shared/RequirementWorkspace';
import { Profile } from '../features/profile';


/**
 * Returns true if the logged-in user is a Team Lead / Manager / Admin.
 * Uses the same comprehensive check as RFPAnalysisResult.jsx (designation + role).
 */
const isTeamLead = (user) => {
    const designation = (user?.designation || '').toLowerCase();
    const role = (user?.role || '').toLowerCase();
    return (
        designation.includes('lead') || designation.includes('manager') || designation.includes('admin') ||
        role.includes('lead') || role.includes('manager') || role.includes('admin')
    );
};

/**
 * Authenticated routes — rendered when the user is logged in.
 */
export const AuthenticatedRoutes = ({ user, globalSearch, onUserUpdate }) => (
    <Routes>
        <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
        />
        <Route
            path="/dashboard"
            element={<Dashboard user={user} />}
        />
        <Route path="/proposals" element={<BidManagerDashboard user={user} globalSearch={globalSearch} />} />
        {/* /my-proposals is for Team Members only — Team Leads are redirected to /proposals */}
        <Route
            path="/my-proposals"
            element={
                isTeamLead(user)
                    ? <Navigate to="/proposals" replace />
                    : <EditorDashboard user={user} globalSearch={globalSearch} />
            }
        />
        <Route path="/proposals/new" element={<RFPIntake />} />

        <Route path="/proposals/analysis-result/:rfpId" element={<RfpDetails />} />
        <Route path="/proposals/analysis-result/:rfpId/secid/:secId" element={<RfpDetails />} />
        <Route path="/proposals/ppt-customization/:rfpId" element={<PPTCustomize />} />
        <Route path="/proposals/ppt-customization/:rfpId/theme" element={
            isTeamLead(user) ? <PPTThemePage /> : <Navigate to="/dashboard" replace />
        } />
        <Route path="/themes" element={
            isTeamLead(user) ? <PPTThemePage /> : <Navigate to="/dashboard" replace />
        } />
        <Route path="/projects" element={<Projects user={user} />} />
        <Route path="/requirements" element={<Requirements />} />
        <Route path="/library" element={<ContentLibrary />} />
        <Route path="/team" element={<Team />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/audit-logs" element={<AuditLogs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/profile" element={<Profile user={user} onUserUpdate={onUserUpdate} />} />
        <Route path="/presentation" element={<PresentationConfig />} />
        <Route path="/users-roles" element={<UsersRoles />} />
        <Route path="/users-roles/:userId" element={<UserDetails />} />
        <Route path="/org/users/:userId" element={<UserDetails />} />
        <Route path="*" element={<BidManagerDashboard user={user} />} />
    </Routes>
);

/**
 * Public (unauthenticated) routes.
 */
export const PublicRoutes = ({ onLogin }) => (
    <Routes>
        <Route path="/login" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="/signup" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="/forgot-password" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="/set-password" element={<UnifiedAuth onLogin={onLogin} />} />
        <Route path="*" element={<UnifiedAuth onLogin={onLogin} />} />
    </Routes>
);
